/**
 * Standards Check Ready — qualified-ADI prep dashboard.
 *
 * Reuses the same engine as PDI Part-3 readiness but framed for ADIs facing
 * DVSA Standards Check. Confidence framing, countdown, 4-stage prep checklist,
 * Top 3 next focus, and a clear CTA into Lesson Footage Review.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, ArrowLeft, Sparkles, Calendar, Target, ChevronRight,
  CheckCircle2, Circle, Upload, Award, Clock, Zap,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { trackFunnel } from "../utils/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

const BAND_META = {
  ready:      { color: "emerald", label: "READY",      sub: "Walk in confident" },
  close:      { color: "gold",    label: "CLOSE",      sub: "On track — keep refining" },
  developing: { color: "amber",   label: "DEVELOPING", sub: "Building solidly" },
  early:      { color: "zinc",    label: "EARLY",      sub: "Plenty of headroom" },
};

export default function StandardsCheckReady() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState(false);
  const [dateInput, setDateInput] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/standards-check/dashboard`, { withCredentials: true });
      setDash(data);
      setDateInput(data.check_date || "");
      // Funnel auto-fires for the "ready" state when readiness ≥ 80
      if ((data?.readiness_score ?? 0) >= 80) {
        trackFunnel("standards_check", "ready_state_reached", { score: data.readiness_score });
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Funnel: page viewed
  useEffect(() => { trackFunnel("standards_check", "viewed"); }, []);

  const saveDate = async () => {
    if (!dateInput) return;
    try {
      await axios.post(`${API}/api/standards-check/date`, { check_date: dateInput }, { withCredentials: true });
      setEditingDate(false);
      trackFunnel("standards_check", "date_set", { check_date: dateInput });
      toast.success("Standards Check date saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save date");
    }
  };

  const clearDate = async () => {
    try {
      await axios.delete(`${API}/api/standards-check/date`, { withCredentials: true });
      setEditingDate(false);
      setDateInput("");
      await load();
    } catch { toast.error("Couldn't clear date"); }
  };

  if (loading) {
    return <div className="min-h-dvh no-overflow-x bg-[#050505] grid place-items-center text-zinc-500 text-sm">Loading…</div>;
  }
  const band = BAND_META[dash?.confidence_band || "early"];

  return (
    <div className="min-h-dvh no-overflow-x bg-[#050505] text-white font-outfit pb-24" data-testid="std-check-page">
      <header className="sticky top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#050505]/85 border-b border-white/[0.07]">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="sc-back" onClick={() => goBack()} className="p-2 -ml-2 rounded-lg hover:bg-white/5">
            <ArrowLeft className="w-4 h-4 text-zinc-400" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">Standards Check Ready</p>
            <p className="text-[10.5px] text-zinc-500 uppercase tracking-[0.18em]">For qualified ADIs</p>
          </div>
          <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* HERO + COUNTDOWN */}
        <section data-testid="sc-hero" className="rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#16120a] via-[#0c0c0c] to-[#0a0a0a] p-5 md:p-7 guild-card-glow">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-3 inline-flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Confidence engine
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold leading-tight">
            Walk into your Standards Check<br />
            <span className="text-gold-gradient">already knowing you're ready.</span>
          </h1>
          <p className="mt-3 text-[13.5px] text-zinc-300 leading-relaxed max-w-xl">
            The same 17-DVSA-competency engine your trainees use — applied to your own teaching. Upload practice lessons, see what you're nailing, get prescribed next focus.
          </p>

          {/* Countdown */}
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2 inline-flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> Your Standards Check
              </div>
              {!editingDate && dash?.check_date ? (
                <>
                  <div data-testid="sc-countdown" className="font-display text-3xl font-bold text-white">
                    {dash.countdown_days >= 0 ? `${dash.countdown_days} days` : "Date passed"}
                  </div>
                  <div className="text-[12px] text-zinc-400 mt-1">{dash.check_date} · {dash.countdown_weeks} weeks</div>
                  <button data-testid="sc-edit-date" onClick={() => setEditingDate(true)} className="mt-3 text-[11.5px] text-[#F3E5AB] hover:text-white">
                    Change date
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  <input
                    type="date"
                    data-testid="sc-date-input"
                    value={dateInput}
                    onChange={(e) => setDateInput(e.target.value)}
                    className="w-full bg-white/[0.04] border border-white/15 rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-[#D4AF37]"
                  />
                  <div className="flex gap-2">
                    <Button data-testid="sc-save-date" onClick={saveDate} className="h-9 px-3 bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[12px] font-bold rounded-md">
                      Save
                    </Button>
                    {dash?.check_date && (
                      <Button data-testid="sc-clear-date" onClick={clearDate} variant="outline" className="h-9 px-3 bg-transparent border border-white/15 text-zinc-300 text-[12px] font-semibold rounded-md">
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2 inline-flex items-center gap-1.5">
                <Target className="w-3 h-3" /> Readiness
              </div>
              <div data-testid="sc-readiness-score" className="font-display text-3xl font-bold text-white">
                {dash?.readiness_score ?? 0}
                <span className="text-base text-zinc-500 font-normal ml-1">/100</span>
              </div>
              <div className={`mt-1 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] rounded-full px-2 py-0.5 border ${
                band.color === "emerald" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                  : band.color === "gold"    ? "bg-[#D4AF37]/15 text-[#F3E5AB] border-[#D4AF37]/35"
                  : band.color === "amber"   ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                  :                            "bg-white/[0.04] text-zinc-400 border-white/10"
              }`}>
                {band.label}
              </div>
              <div className="text-[12px] text-zinc-400 mt-2">{dash?.headline}</div>
              <div className="text-[10.5px] text-zinc-500 mt-1">
                {dash?.competencies_tested}/{dash?.competencies_total} competencies tested
              </div>
            </div>
          </div>
        </section>

        {/* PRIMARY CTA — upload lesson */}
        <section data-testid="sc-cta-upload" className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-amber-500/10 to-amber-900/10 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
            <Upload className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-white">Upload your next practice lesson</p>
            <p className="text-[11.5px] text-zinc-400 mt-0.5">AI grades it against the same rubric the DVSA examiner uses.</p>
          </div>
          <Button data-testid="sc-upload-btn" onClick={() => { trackFunnel("standards_check", "practice_review_clicked"); nav("/lesson-review"); }} className="h-10 px-4 bg-gradient-to-b from-[#F3E5AB] to-[#D4AF37] hover:from-[#FAF0C0] hover:to-[#E5C04A] text-[#050505] text-[12px] font-bold rounded-md">
            Open Practice Review
          </Button>
        </section>

        {/* 4-STAGE PREP CHECKLIST */}
        <section data-testid="sc-stages" className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-3 inline-flex items-center gap-2">
            <Zap className="w-3 h-3" /> Your 4-stage prep
          </div>
          <ul className="space-y-2.5">
            {(dash?.stages || []).map((s, i) => (
              <li key={s.id} data-testid={`sc-stage-${i}`} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                {s.done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                )}
                <span className={`text-[13px] ${s.done ? "text-zinc-400 line-through" : "text-white font-semibold"}`}>
                  {s.label}
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* USAGE STATS */}
        <section data-testid="sc-stats" className="grid grid-cols-3 gap-3">
          {[
            { label: "Practice lessons", value: dash?.review_count ?? 0, icon: Upload },
            { label: "Footage minutes", value: dash?.review_total_minutes ?? 0, icon: Clock },
            { label: "Recurring weak", value: dash?.recurring_weak_areas?.length ?? 0, icon: Target },
          ].map(({ label, value, icon: Icon }, i) => (
            <div key={i} className="rounded-xl border border-white/[0.07] bg-white/[0.025] p-3 text-center">
              <Icon className="w-4 h-4 text-[#D4AF37] mx-auto mb-1" />
              <div className="font-display text-xl font-bold text-white">{value}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-[0.16em] mt-0.5">{label}</div>
            </div>
          ))}
        </section>

        {/* TOP 3 NEXT FOCUS */}
        {dash?.next_focus?.length > 0 && (
          <section data-testid="sc-next-focus">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-3 inline-flex items-center gap-2">
              <Target className="w-3 h-3" /> Top 3 next focus
            </div>
            <div className="space-y-2.5">
              {dash.next_focus.map((p, i) => (
                <button
                  key={i}
                  data-testid={`sc-focus-${i}`}
                  onClick={() => nav(p.mastery_skill_url)}
                  className="w-full text-left flex items-start gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.025] hover:border-[#D4AF37]/35 p-4 transition"
                >
                  <span className="w-9 h-9 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center text-[12px] font-bold text-[#F3E5AB] flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-semibold text-white">{p.mastery_skill_title}</p>
                    <p className="text-[11.5px] text-zinc-400 mt-0.5">{p.mastery_skill_summary}</p>
                    <p className="text-[10.5px] text-zinc-500 mt-1">Triggered by: <span className="text-zinc-300">{p.competency_label}</span></p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600 mt-1 flex-shrink-0" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* CPD PEEK */}
        <section data-testid="sc-cpd-peek" className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4 flex items-center gap-3">
          <Award className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-white">CPD record</p>
            <p className="text-[11.5px] text-zinc-400">Every Standards-Check practice session counts toward your CPD log.</p>
          </div>
          <Button data-testid="sc-open-cpd" onClick={() => nav("/cpd")} variant="outline" className="h-9 px-3 bg-transparent border border-white/15 hover:bg-white/5 text-white text-[12px] font-semibold rounded-md">
            Open CPD
          </Button>
        </section>
      </div>
    </div>
  );
}
